import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { apiUrl } from '../config';

/**
 * Google Sign-In Button Component
 *
 * Integrates Google OAuth 2.0 sign-in with backend authentication.
 *
 * Security:
 * - Uses @react-oauth/google for secure token handling
 * - Sends Google ID token to backend for server-side verification
 * - Backend verifies token signature, expiry, and audience
 * - Never exposes sensitive Google credentials to frontend
 *
 * @param {Object} props
 * @param {string} props.loginMode - 'doctor' or 'patient'
 * @param {Function} props.onSuccess - Callback on successful authentication
 * @param {Function} props.onError - Callback on error
 * @param {Function} props.onLoadingChange - Callback when loading state changes
 * @param {string} props.className - Optional CSS class for styling
 */
const GoogleSignInButton = ({ loginMode, onSuccess, onError, onLoadingChange, className }) => {
  const [isLoading, setIsLoading] = useState(false);
  const containerClass = ['google-auth-shell', className].filter(Boolean).join(' ');

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    if (onLoadingChange) {
      onLoadingChange(true);
    }

    try {
      // credentialResponse.credential contains the Google ID token
      // This token is sent to backend for server-side verification
      const response = await fetch(apiUrl('/api/auth/google'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialResponse.credential,
          loginMode: loginMode
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Store tokens in localStorage
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('loginMode', loginMode);

        // Call success callback
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        // Authentication failed
        console.error('Google authentication failed:', data.error);
        if (onError) {
          onError(data.error || 'Google authentication failed');
        }
      }
    } catch (error) {
      console.error('Error during Google authentication:', error);
      if (onError) {
        onError('Network error. Please try again.');
      }
    } finally {
      setIsLoading(false);
      if (onLoadingChange) {
        onLoadingChange(false);
      }
    }
  };

  const handleGoogleError = () => {
    console.error('Google Sign-In was unsuccessful');
    if (onError) {
      onError('Google Sign-In failed. Please try again.');
    }
  };

  return (
    <div className={containerClass}>
      {isLoading ? (
        <div className="google-auth-loading">
          <span className="google-auth-spinner" aria-hidden="true" />
          <span>Signing in...</span>
        </div>
      ) : (
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          auto_select={false}
          theme="filled_black"
          size="large"
          text="continue_with"
          shape="pill"
          logo_alignment="left"
          width="100%"
        />
      )}
    </div>
  );
};

export default GoogleSignInButton;
